import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, ChevronRight, FileText, BarChart3, Workflow, AlertCircle, Brain, Building2, Wine, Calculator, Container, Stamp, TrendingUp, Users, BarChart, Award, QrCode, Thermometer, ShieldAlert, Beaker, MapPin, Calendar, Box, Banknote, Tag, Leaf, ClipboardCheck } from "lucide-react";

const ICON_MAP = {
  "supplier-onboarding": Building2,
  "compliance": Wine,
  "quotation": Calculator,
  "container": Container,
  "mapa-workflow": Stamp,
  "commission": TrendingUp,
  "crm-brazil": Users,
  "dashboard-management": BarChart,
  // Wine
  "wine-cellar": Wine,
  "wine-lab": Beaker,
  "wine-vintage": Calendar,
  "wine-awards": Award,
  "wine-sommelier": Sparkles,
  "wine-authenticity": QrCode,
  // Food
  "food-coldchain": Thermometer,
  "food-haccp": ShieldAlert,
  "food-dop": Leaf,
  "food-shelflife": ClipboardCheck,
  "food-allergens": MapPin,
  "food-recipes": Box,
  // Cross
  "cross-dta": Container,
  "cross-adduma": Stamp,
  "cross-pricing": Tag,
  "cross-samples": Box,
  "cross-fx": Banknote,
  "cross-brand": ShieldAlert,
};

const MODULES = {
  "supplier-onboarding": {
    n: "01", title: "Supplier Onboarding",
    obj: "Attivare nuovi fornitori europei.",
    sections: [
      { title: "Anagrafica fornitore", items: ["Ragione sociale", "Partita IVA", "Paese", "Contatti", "Referenti commerciali"] },
      { title: "Contratti", items: ["Esclusiva territoriale", "Condizioni commerciali", "Metodo pagamento", "MOQ", "Incoterm"] },
      { title: "Documenti", items: ["Cataloghi", "Certificazioni", "Schede tecniche", "Certificati export"] },
    ],
    kpi: ["Tempo onboarding", "Documentazione completa", "Fornitori attivi"],
  },
  "compliance": {
    n: "02", title: "Product Compliance Hub",
    obj: "Centralizzare tutti gli SKU idonei per il Brasile.",
    sections: [
      { title: "Product Master", items: ["SKU", "Brand", "Categoria", "Origine", "Packaging", "Shelf life"] },
      { title: "Regulatory Data", items: ["MAPA category", "NCM code", "Alcohol %", "Volume", "Ingredienti", "Allergeni"] },
      { title: "Document Repository", items: ["Technical Sheet", "COA", "Certificate of Origin", "Label artwork"] },
    ],
    workflow: ["NEW", "DOCUMENTS PENDING", "UNDER REVIEW", "LAB TEST", "APPROVED", "COMMERCIAL ACTIVE"],
    kpi: ["SKU approvati", "SKU in revisione", "Tempo approvazione"],
    liveLink: "/admin/imports", liveLinkLabel: "Apri Import & Documenti (LIVE)",
  },
  "quotation": {
    n: "03", title: "Quotation & Deal Desk",
    obj: "Calcolare prezzi, costi e margini.",
    sections: [
      { title: "Cost Simulation", items: ["EXW", "FOB", "Freight", "Insurance", "Customs", "Broker fees"] },
      { title: "Margin Calculation", items: ["Prezzo vendita", "− Costo fornitore", "= Margine"] },
      { title: "Quotation Management", items: ["Preventivi", "Revisioni", "Storico offerte"] },
    ],
    kpi: ["Margine per SKU", "Margine per cliente", "Tasso conversione offerte"],
  },
  "container": {
    n: "04", title: "Container & Shipping",
    obj: "Consolidare merce da più fornitori.",
    sections: [
      { title: "Container Planning", items: ["CBM", "Peso", "Volume", "Temperatura"] },
      { title: "Consolidation", items: ["Fornitore A", "Fornitore B", "Fornitore C", "→ Container unico"] },
      { title: "Shipping Tracking", items: ["Booking", "ETD", "ETA", "Vessel tracking"] },
      { title: "Export Documents", items: ["Commercial Invoice", "Packing List", "BL", "COO", "COA"] },
    ],
    kpi: ["Fill rate", "Freight cost / unit", "Transit time"],
  },
  "mapa-workflow": {
    n: "05", title: "MAPA Workflow",
    obj: "Gestire compliance importazione Brasile.",
    sections: [
      { title: "Dossier Import", items: ["Invoice", "Packing list", "COA", "Labels", "Certificates"] },
      { title: "LI / SISCOMEX", items: ["Preparazione pratica", "Invio broker", "Tracking approvazione"] },
      { title: "Alert", items: ["Documenti mancanti", "Pratiche bloccate", "Exigências"] },
    ],
    workflow: ["SHIPMENT CREATED", "DOCUMENT PACK READY", "BROKER REVIEW", "LI SUBMITTED", "MAPA ANALYSIS", "APPROVED"],
    kpi: ["Giorni approvazione", "Pratiche aperte", "Pratiche chiuse"],
  },
  "commission": {
    n: "06", title: "Commission Engine",
    obj: "Calcolare commissioni.",
    sections: [
      { title: "Commission Formula", items: ["Commissione = FOB × %", "oppure", "Margine = Sell Price − Cost"] },
      { title: "Report", items: ["Per fornitore", "Per cliente", "Per container", "Per trimestre"] },
    ],
    kpi: ["Commissioni mensili", "Margine totale", "Top clienti"],
  },
  "crm-brazil": {
    n: "07", title: "CRM B2B Brazil",
    obj: "Gestire clienti brasiliani.",
    sections: [
      { title: "Tipologie clienti", items: ["Importatori", "Distributori", "Horeca", "Retail"] },
      { title: "Funzioni", items: ["Follow up", "Meeting", "Campionature", "Storico ordini"] },
    ],
    workflow: ["LEAD", "SAMPLE", "QUOTATION", "NEGOTIATION", "PO", "SHIPMENT", "REORDER"],
    kpi: ["Conversion rate", "Clienti attivi", "Riordini"],
  },
  "dashboard-management": {
    n: "08", title: "Management Dashboard",
    obj: "Cockpit unico KPI cross-funzionali.",
    sections: [
      { title: "Procurement", items: ["Fornitori attivi", "Lead time"] },
      { title: "Compliance", items: ["SKU approvati", "Tempo MAPA"] },
      { title: "Logistics", items: ["Container in transito", "Costi logistici"] },
      { title: "Sales", items: ["Fatturato", "Margini", "Clienti attivi"] },
      { title: "Finance", items: ["Commissioni", "Cash flow", "Exposure EUR/BRL"] },
    ],
    kpi: ["KPI real-time"],
    liveLink: "/admin", liveLinkLabel: "Apri dashboard Admin (BASE LIVE)",
  },
  // ==================== WINE MODULES ====================
  "wine-cellar": {
    n: "W1", title: "Cellar Inventory",
    obj: "Cantina virtuale aggregata multi-fornitore con tracciamento per cantina, annata e DOC/DOCG.",
    sections: [
      { title: "Stock Management", items: ["Bottiglie disponibili per cantina", "Suddivisione per annata e DOC/DOCG", "Magazzini europei mappati"] },
      { title: "Allocation", items: ["Lotti riservati per container", "Pre-ordini & opzioni", "Limited release & ex-cellar tracking"] },
      { title: "Movimentazione", items: ["Carico fornitore → magazzino consolidamento", "Scarico → container Brasile", "Reso & merci difettose"] },
    ],
    kpi: ["Bottiglie disponibili", "Rotazione magazzino", "% Allocazione futura"],
  },
  "wine-lab": {
    n: "W2", title: "Wine Lab Test Manager",
    obj: "Coordinare le analisi chimiche obbligatorie MAPA con laboratori accreditati EU/BR.",
    sections: [
      { title: "Parametri standard", items: ["pH · acidità totale", "Solfiti liberi/totali", "Zuccheri residui", "Alcool · estratto secco"] },
      { title: "Workflow Lab", items: ["Richiesta analisi", "Invio campione laboratorio", "Refertazione · COA digitale", "Auto-link al SKU compliance"] },
      { title: "Laboratori accreditati", items: ["Italia: ENOLAB, ICEA", "Brasile: LANAGRO MAPA", "Cross-validation per export"] },
    ],
    workflow: ["RICHIESTA", "CAMPIONE INVIATO", "ANALISI IN CORSO", "COA RILASCIATO", "LINKED TO SKU"],
    kpi: ["Test passati al primo tentativo", "Tempo medio refertazione", "Costo per analisi"],
  },
  "wine-vintage": {
    n: "W3", title: "Vintage & Allocation Tracker",
    obj: "Storico annate per cantina e gestione allocazione futura.",
    sections: [
      { title: "Storico Annate", items: ["Punteggi annata per regione", "Volumi prodotti per cantina", "Trend qualitativi pluriennali"] },
      { title: "Allocazione Futura", items: ["Pre-ordini per importatore", "Opzioni vincolanti su uscite", "Limited release allocazione"] },
      { title: "En-Primeur", items: ["Grand Cru italiani in pre-rilascio", "Calendar uscite 2026-2028", "Tier importatore prioritario"] },
    ],
    kpi: ["Annate gestite", "% Allocazione completata", "Pre-ordini valore"],
  },
  "wine-awards": {
    n: "W4", title: "Awards & Certifications",
    obj: "Tracciare punteggi, premi e certificazioni per posizionamento premium su ADDUMA.",
    sections: [
      { title: "Critici Wine", items: ["Gambero Rosso (Tre Bicchieri)", "Decanter (90+ punti)", "Wine Spectator", "James Suckling · Robert Parker"] },
      { title: "Certificazioni", items: ["Biologico EU · Demeter biodinamico", "Vegan · Kosher", "DOCG · DOC · IGT"] },
      { title: "Marketing Use", items: ["Auto-tagging su scheda compliance", "Premium shelf positioning BR", "Tasting notes con references"] },
    ],
    kpi: ["SKU premiati", "Score medio portfolio", "Premium tier %"],
  },
  "wine-sommelier": {
    n: "W5", title: "AI Sommelier Notes",
    obj: "Generare note di degustazione e pairing con cucina brasiliana via GPT-5.2.",
    sections: [
      { title: "Tasting Notes", items: ["Naso · palato · finale", "IT · EN · PT-BR auto-traduzione", "Lunghezza variabile (web · catalog · label)"] },
      { title: "Pairing brasiliano", items: ["Feijoada · churrasco · moqueca", "Cucina nordestina · sud", "Pairing per regione climatica"] },
      { title: "Output", items: ["JSON strutturato per import", "PDF brandizzato per importatore", "Push automatico su scheda BR"] },
    ],
    kpi: ["Note generate", "SKU coperti", "Engagement importatori"],
  },
  "wine-authenticity": {
    n: "W6", title: "Bottle Authenticity & QR",
    obj: "Anti-counterfeit con QR univoco e story-tag bilingue.",
    sections: [
      { title: "QR System", items: ["Codice univoco per bottiglia / pallet", "Inquadra → autenticità", "Geo-tracking bottiglie attivate"] },
      { title: "Story Tag", items: ["Storia cantina · vigneto · vignaiolo", "IT/PT bilingue auto-tradotto", "Photo-essay vendemmia"] },
      { title: "Premium NFT", items: ["NFT seal per high-end (>€500)", "Provenance immutabile", "Transfer ownership tracciato"] },
    ],
    kpi: ["Bottiglie verificate", "Frodi rilevate", "Engagement post-acquisto"],
  },
  // ==================== FOOD MODULES ====================
  "food-coldchain": {
    n: "F1", title: "Cold Chain Monitor",
    obj: "Monitorare la catena del freddo nei container salumi/formaggi/pasta fresca.",
    sections: [
      { title: "Sensori IoT", items: ["Temperatura · umidità", "Geolocalizzazione GPS", "Frequenza letture: 5 min"] },
      { title: "Alert", items: ["Deviazione temperatura > 2°C", "Notifica push agli operatori BR", "Auto-claim assicurativo"] },
      { title: "Compliance Sanitario", items: ["Report MAPA / ANVISA pre-sbarco", "Storico per audit ispettivi", "Certificato igienico-sanitario"] },
    ],
    workflow: ["SENSORE INSTALLATO", "MONITORING ATTIVO", "ALERT DEVIAZIONE", "CLAIM IF NEEDED", "REPORT ARCHIVIATO"],
    kpi: ["Container conformi %", "Eccezioni mensili", "Tempo risoluzione"],
  },
  "food-haccp": {
    n: "F2", title: "HACCP Compliance",
    obj: "Sicurezza alimentare ANVISA con piani HACCP per categoria.",
    sections: [
      { title: "Piani HACCP", items: ["Salumi stagionati", "Formaggi DOP", "Pasta fresca / secca", "Conserve & oli"] },
      { title: "CCP (Critical Control Points)", items: ["Stoccaggio · trasporto · sbarco", "Temperatura · pH · aw", "Sigilli & manipolazione"] },
      { title: "Audit", items: ["Documentazione audit-ready", "Sync ANVISA RDC vigenti", "Storico ispezioni"] },
    ],
    kpi: ["Audit superati", "Non-conformità", "Tempo risoluzione"],
  },
  "food-dop": {
    n: "F3", title: "DOP / IGP / STG Tracker",
    obj: "Gestire le specialità tradizionali italiane protette per posizionamento premium BR.",
    sections: [
      { title: "Indicazioni Geografiche", items: ["DOP: Parmigiano Reggiano, Prosciutto di Parma…", "IGP: Mortadella Bologna, Bresaola Valtellina…", "STG: Mozzarella, Pizza Napoletana"] },
      { title: "Disciplinari", items: ["Zona produzione", "Tecnica obbligatoria", "Controlli consorzio"] },
      { title: "Storytelling Premium", items: ["Storia consorzio · territorio", "Auto-content per importatore BR", "Anti italian-sounding marketing"] },
    ],
    kpi: ["DOP attive", "Coverage regioni IT", "Premium uplift %"],
  },
  "food-shelflife": {
    n: "F4", title: "Shelf Life & Lotti",
    obj: "Best-before per SKU + tracciabilità lotti in ottica priorità export.",
    sections: [
      { title: "Best-Before", items: ["Per SKU + lotto", "Soglie di alert (90/60/30 gg)", "Highlight lotti critici"] },
      { title: "Lotti deperibili", items: ["Priorità export aerei vs container", "Allocazione canali fast-mover", "Discount automatico shelf-life critica"] },
      { title: "Traceability", items: ["Lotto → ingrediente → fornitore", "Recall instantaneo per lotto", "Storico movimentazioni"] },
    ],
    kpi: ["Lotti in scadenza", "Rotazione magazzino", "% Lotti in priorità export"],
  },
  "food-allergens": {
    n: "F5", title: "Allergeni & Traceability",
    obj: "Etichettatura ANVISA-compliant (RDC 26/2015) con allergeni e ingredienti in PT-BR.",
    sections: [
      { title: "Allergeni ANVISA", items: ["12 allergeni obbligatori RDC 26/2015", "Glutine · lattosio · solfiti · frutta a guscio", "Cross-contamination warning"] },
      { title: "Etichetta PT-BR", items: ["Auto-traduzione lista ingredienti", "Tabella nutrizionale ANVISA", "QR rimando a scheda IT originale"] },
      { title: "Traceability", items: ["Lotto → ingrediente → produttore", "Push automatico su recall", "Storico modifiche etichetta"] },
    ],
    kpi: ["SKU labelizzati", "Errori etichetta rilevati", "Tempo update label"],
  },
  "food-recipes": {
    n: "F6", title: "Recipe & Pairing Hub",
    obj: "Box ricetta + pairing AI per up-sell B2C italiano e showcase su importatore BR.",
    sections: [
      { title: "AI Recipes B2C", items: ["GPT-5.2 genera ricette autentiche", "Box dedicate per ricetta", "Stagionalità & territorialità"] },
      { title: "Pairing Wine + Food", items: ["AI sommelier collegato a Box food", "Suggerimento bottiglia per ricetta", "Cross-sell wine-food per ordine"] },
      { title: "Showcase Brasile", items: ["Catalogo ricette in PT-BR", "Eventi cooking-class per Horeca", "Demo prodotti italiani con chef BR"] },
    ],
    kpi: ["Ricette generate", "Box conversion %", "Avg. ricetta → ordine"],
    liveLink: "/ai-ricette", liveLinkLabel: "Apri AI Recipes (B2C LIVE)",
  },
  // ==================== CROSS MODULES ====================
  "cross-dta": {
    n: "X1", title: "DTA Brasil Connector",
    obj: "Integrazione bidirezionale con DTA Brasil per logistica & dogana.",
    sections: [
      { title: "Sync Booking", items: ["Push booking automatico", "Confirm ETD · ETA", "Real-time vessel tracking"] },
      { title: "Documenti dogana", items: ["Auto-export Commercial Invoice", "Packing List · BL · COO", "DUS / DUE coordination"] },
      { title: "Status integrato", items: ["Stato pratica DTA → console", "Alert blocchi dogana", "ETA Porto Santos / Itajaí"] },
    ],
    kpi: ["Container DTA mensili", "Lead time medio", "Costo per TEU"],
  },
  "cross-adduma": {
    n: "X2", title: "ADDUMA Compliance Bridge",
    obj: "Connector con ADDUMA Brasil per gestione compliance LI/SISCOMEX.",
    sections: [
      { title: "Push automatico dossier", items: ["MAPA dossier auto-export ad ADDUMA", "Documenti versionati", "Audit trail completo"] },
      { title: "Status SISCOMEX", items: ["Stato pratica LI real-time", "Tracking Receita Federal", "Alert exigências"] },
      { title: "Bidirezionale", items: ["Note ADDUMA → operations IT", "Risposta documenti integrativi", "SLA monitoring"] },
    ],
    workflow: ["DOSSIER PRONTO", "PUSH AD ADDUMA", "BROKER REVIEW", "SISCOMEX SUBMITTED", "MAPA APPROVAL", "RELEASE PORTO"],
    kpi: ["Pratiche ADDUMA", "Tempo medio approvazione", "Exigências mensili"],
  },
  "cross-pricing": {
    n: "X3", title: "Pricing Tier Manager",
    obj: "Listini differenziati per tipologia cliente con margine target per canale.",
    sections: [
      { title: "Tier Cliente", items: ["Importatore (Trinacria, Dora Gourmet)", "Distributore (Gruppo Alimentare Italiano)", "Horeca · Retail premium · Boutique"] },
      { title: "Strategia prezzo", items: ["Sconti volume scaglionati", "Campagne promo trimestrali", "Prezzi annata limitata"] },
      { title: "Margin Protection", items: ["Margine target per canale", "Alert sotto-soglia", "Approval workflow per deroghe"] },
    ],
    kpi: ["Margine medio per tier", "% sconti applicati", "Velocità conversion"],
  },
  "cross-samples": {
    n: "X4", title: "Sample & Tasting Manager",
    obj: "Gestire campionature inviate e organizzare eventi tasting in Brasile.",
    sections: [
      { title: "Sample Tracking", items: ["Storico campioni per cliente", "Costi campionatura", "Conversion sample → PO"] },
      { title: "Tasting Events", items: ["Eventi Horeca · sommelier · stampa", "Calendar trimestrale BR", "ROI per evento"] },
      { title: "Follow-up", items: ["CRM auto-task post-evento", "Templates email post-tasting", "Lead nurturing"] },
    ],
    kpi: ["Tasso conversion sample → ordine", "Eventi annuali", "ROI per evento"],
  },
  "cross-fx": {
    n: "X5", title: "FX Hedging EUR↔BRL",
    obj: "Tracking esposizione cambio EUR/BRL e simulator copertura forward.",
    sections: [
      { title: "Esposizione real-time", items: ["Crediti BRL aperti", "Costi EUR fornitori", "Exposure netta giornaliera"] },
      { title: "Hedging Tools", items: ["Forward contract simulator", "Suggerimento copertura ottimale", "Stop-loss & target rate"] },
      { title: "Margin Protection", items: ["P&L cambio storico", "Alert volatilità", "Dashboard cash flow"] },
    ],
    kpi: ["Exposure netta", "Hedge ratio", "P&L cambio MTD"],
  },
  "cross-brand": {
    n: "X6", title: "Brand Protection",
    obj: "Tutela marchi italiani in Brasile contro contraffazione e italian-sounding.",
    sections: [
      { title: "INPI Brasile", items: ["Marchi registrati per fornitore", "Watch new applications", "Alert conflitti"] },
      { title: "Italian-sounding", items: ["Monitoring prodotti tarocchi", "Catene retail BR sotto osservazione", "Database mensile concorrenza"] },
      { title: "Legal Pipeline", items: ["Escalation a studio legale partner", "Notifiche stragiudiziali", "Storico vertenze chiuse"] },
    ],
    kpi: ["Marchi monitorati", "Alert mensili", "Vertenze risolte"],
  },
};

export default function GestionaleModule() {
  const { slug } = useParams();
  const m = MODULES[slug];
  const Icon = ICON_MAP[slug] || Sparkles;

  if (!m) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <div className="text-center space-y-3">
          <h1 className="editorial-heading text-3xl">Modulo non trovato</h1>
          <Link to="/gestionale/dashboard" className="inline-block text-[#C9A961] underline text-sm">← Torna alla dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`gestionale-module-${slug}`} className="min-h-screen bg-gradient-to-b from-[#0a0a0a] to-[#1a0608] text-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10 md:py-14">
        <Link to="/gestionale/dashboard" data-testid="back-to-dashboard" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-[#C9A961] mb-8">
          <ArrowLeft className="h-3 w-3" /> Torna alla dashboard
        </Link>

        <div className="flex items-start gap-4 mb-2">
          <div className="text-[#C9A961]/40 editorial-heading text-5xl leading-none font-light shrink-0">{m.n}</div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Icon className="h-5 w-5 text-[#C9A961]" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold">Modulo · Gestionale</span>
            </div>
            <h1 className="editorial-heading text-4xl md:text-5xl leading-tight">{m.title}</h1>
            <p className="text-white/60 mt-3 max-w-2xl">{m.obj}</p>
          </div>
        </div>

        {m.liveLink ? (
          <Link to={m.liveLink} data-testid="module-live-cta" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[#C9A961] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-white">
            <Sparkles className="h-3.5 w-3.5" /> {m.liveLinkLabel}
          </Link>
        ) : (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.25em] text-white/60">
            <AlertCircle className="h-3 w-3" /> Modulo in roadmap · UI in arrivo
          </div>
        )}

        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {m.sections.map((s, i) => (
            <div key={i} className="bg-black/40 border border-white/10 rounded-sm p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-3">{s.title}</div>
              <ul className="space-y-2">
                {s.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-white/75">
                    <ChevronRight className="h-3.5 w-3.5 text-[#C9A961]/60 mt-1 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {m.workflow && (
          <div className="mt-8 bg-black/40 border border-[#C9A961]/30 rounded-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-4 flex items-center gap-2">
              <Workflow className="h-3 w-3" /> Workflow
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {m.workflow.map((w, i) => (
                <React.Fragment key={i}>
                  <span className="px-3 py-1.5 bg-[#C9A961]/10 border border-[#C9A961]/30 text-[10px] uppercase tracking-[0.2em] text-white">{w}</span>
                  {i < m.workflow.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-[#C9A961]/50" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 bg-black/40 border border-white/10 rounded-sm p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] font-bold mb-3 flex items-center gap-2">
            <BarChart3 className="h-3 w-3" /> KPI
          </div>
          <div className="flex flex-wrap gap-2">
            {m.kpi.map((k, i) => (
              <span key={i} className="text-[10px] uppercase tracking-wider px-3 py-1.5 bg-white/5 border border-white/10 text-white/70">{k}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
